import axios from 'axios';
import { toast } from 'react-toast';
import { isNetworkError, notifyNetworkError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_BASE_URL;
// Global loading state management
let loadingContext = null;
let sessionExpiredHandled = false;

const SESSION_EXPIRED_MESSAGE =
  'Session expired. Please log in again to continue your onboarding.';

export const setLoadingContext = (context) => {
  loadingContext = context;
};

export const resetSessionExpiredFlag = () => {
  sessionExpiredHandled = false;
};

const isAuthRoute = () => {
  const path = window.location.pathname;
  return (
    path.includes('/auth/') ||
    path.includes('/login') ||
    path.includes('/get-started/auth')
  );
};

const handleSessionExpired = () => {
  localStorage.removeItem('isAuthenticated');
  localStorage.removeItem('user');
  localStorage.removeItem('robitest_onboarding_draft_v1');

  if (sessionExpiredHandled) {
    return;
  }

  sessionExpiredHandled = true;
  toast.hideAll();
  toast.error(SESSION_EXPIRED_MESSAGE);
};


const axiosInstance = axios.create({
  baseURL: API_BASE_URL, 
  // baseURL: "http://10.5.49.119:8000",  
  // baseURL: "http://3.139.107.45:8001",
  // baseURL: "http://localhost:8001",
  // baseURL: "http://10.5.49.119:8000",
  headers: { 'Content-Type': 'application/json' }
});

axiosInstance.interceptors.request.use(
  config => {
    // Generate unique request ID
    const requestId = Date.now() + Math.random();
    config.metadata = { requestId };
    // Start loading unless opted out per request
    if (loadingContext && !config.skipGlobalLoader) {
      loadingContext.startLoading(requestId);
    }

    const authDataString = JSON.parse(localStorage.getItem('user'));
    if (authDataString) {
      config.headers['token'] = `${authDataString?.accessToken}`;
    }

  
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (typeof config.headers?.delete === 'function') {
        config.headers.delete('Content-Type');
      } else if (config.headers) {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }

    return config;
  },
  error => {
    // Stop loading on request error
    if (
      error.config?.metadata?.requestId &&
      loadingContext &&
      !error.config.skipGlobalLoader
    ) {
      loadingContext.stopLoading(error.config.metadata.requestId);
    }
    return Promise.reject(error);
  }
);

// Response interceptor (handle success and errors globally)
axiosInstance.interceptors.response.use(
  response => {
    // Stop loading on successful response
    if (
      response.config?.metadata?.requestId &&
      loadingContext &&
      !response.config.skipGlobalLoader
    ) {
      loadingContext.stopLoading(response.config.metadata.requestId);
    }
    if (!response.config?.skipGlobalToast) {
      const statusCode = response?.data?.status_code;
      const method = response?.config?.method;
      const isMutatingSuccess =
        statusCode === 201 ||
        (statusCode === 200 && (method === 'post' || method === 'put'));
      const message = response?.data?.message;

      if (isMutatingSuccess && message && String(message).trim()) {
        toast.success(message);
      }
    }

    // Success: Here you can filter/modify the response globally
    return response.data;
    // return response;
  },
  error => {
    // Stop loading on error response
    if (
      error.config?.metadata?.requestId &&
      loadingContext &&
      !error.config.skipGlobalLoader
    ) {
      loadingContext.stopLoading(error.config.metadata.requestId);
    }

    // Global error handling logic
    if (isNetworkError(error)) {
      if (!error.config?.skipGlobalToast) {
        notifyNetworkError();
      }
    } else if (error.response) {
      if (error.response.status === 401 && !isAuthRoute()) {
        handleSessionExpired();
      } else if (error.response.status === 403) {
        // Forbidden, handle accordingly
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
